import { Field, FieldArray, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { Button } from '@vadiun/react-components';
import { FormRepeater } from '@vadiun/react-components';

import { KeyboardTimePicker } from 'formik-material-ui-pickers';
import {
  ChoferTurnoFormSchema,
  initialTurn,
} from '../../models/ChoferFormSchema';
import {faTrashAlt, faEdit} from '@fortawesome/free-solid-svg-icons';
import  FormikAddress, { domicilioValidation } from 'shared/components/FormikAddress';
import { Turno, TurnoPayload } from 'app/chofer/models/Chofer';
import useChoferProfile from 'app/chofer/hooks/useChoferProfile';
import {  useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DomicilioModel } from 'app/domicilio/models/DomicilioModel';

const validationSchema = () => {
  return Yup.array(
    Yup.object().shape({
      direccion: domicilioValidation,
      desde: Yup.mixed().required('Es necesaria una hora de inicio'),
      hasta: Yup.mixed().required('Es necesaria una hora de finalizacion')
    })
  ).test(
    'validacion-horarios',
    function (value) {
      if (!value) return true;

      for (let i = 0; i < value.length; i++) {
        const turno = value[i];
        
        if (i > 0 ){
          const turnoAnterior = value[i - 1];
          if (turno.desde && turnoAnterior.hasta && turno.desde.isBefore(turnoAnterior.hasta, 'minutes')) {
            return this.createError({
              path: `${this.path}.${i}.desde`,
              message: 'Este turno se superpone con el anterior'
            });
          }
        }

      /*   if (turno.hasta && turno.desde && !turno.hasta.isAfter(turno.desde, 'minutes')) {
          return this.createError({
            path: `${this.path}.${i}.hasta`,
            message: 'La hora de finalización debe ser posterior a la hora de inicio',
          });
        } */
      }
      return true;
    }
  );
};

const allDaysEmpty = (turnos: ChoferTurnoFormSchema) => {
  const days = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];
  return days.every(day => turnos[day]?.length === 0);
};

const Schema = Yup.object().shape({
  lunes: validationSchema(),
  martes: validationSchema(),
  miercoles: validationSchema(),
  jueves: validationSchema(),
  viernes: validationSchema(),
  sabado: validationSchema(),
  domingo: validationSchema()
});

const mapDiaToEnglish = (dia: string): string => {
  const diasMap: Record<string, string> = {
    'lunes': 'monday',
    'martes': 'tuesday',
    'miercoles': 'wednesday',
    'jueves': 'thursday',
    'viernes': 'friday',
    'sabado': 'saturday',
    'domingo': 'sunday'
  };
  
  return diasMap[dia.toLowerCase()] || dia;
};

const dias = ['lunes', 'martes', 'miercoles', 'jueves', 'viernes', 'sabado', 'domingo'];

export const obtenerUnicoTurno = (turnos: ChoferTurnoFormSchema): 
  { dia: string, turno: Turno } | null => {
  
  const diasSemana: (keyof ChoferTurnoFormSchema)[] = [
    'lunes', 'martes', 'miercoles', 
    'jueves', 'viernes', 'sabado', 'domingo'
  ];

  let turnoEncontrado: { dia: string, turno: Turno } | null = null;
  let totalTurnos = 0;

  for (const dia of diasSemana) {
    const turnosDia = turnos[dia] || [];
    
    for (const turno of turnosDia) {
      totalTurnos++;
      
      if (totalTurnos > 1) return null;
      
      if (!turnoEncontrado) {
        turnoEncontrado = { dia, turno };
      }
    }
  }

  return totalTurnos === 1 ? turnoEncontrado : null;
};

export const obtenerPrimeraDireccion = (turnos: ChoferTurnoFormSchema): 
  {direccion: DomicilioModel } | null => {
  
  const diasSemana: (keyof ChoferTurnoFormSchema)[] = [
    'lunes', 'martes', 'miercoles', 
    'jueves', 'viernes', 'sabado', 'domingo'
  ];

  for (const dia of diasSemana) {
    const turnosDia = turnos[dia] || [];
    
    if (turnosDia.length > 0) {
      return {
        direccion: turnosDia[0].direccion
      };
    }
  }

  return null;
};

interface Props {
  onGoBack?: () => void;
  initialValues: ChoferTurnoFormSchema;
  goBackButtonTitle?: string;
  driverId: number;
  onTurnosUpdated?: () => void;
  handleFinish?: () => void;
}

const ChoferFormTurno = ({
  goBackButtonTitle = 'Volver',
  driverId,
  onGoBack,
  initialValues ,
  onTurnosUpdated ,
  handleFinish
}: Props ) => {
  const {
    editDriverTurnMutation,
    createDriverTurnMutation,
    choferQuery,
    deleteDriverTurnMutation
  } = useChoferProfile();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const tieneSoloUnTurno = obtenerUnicoTurno(initialValues);
  const primeraDireccionDias = obtenerPrimeraDireccion(initialValues)


  const onSubmitByTurn = async (dia: string, turno: Turno) => {
    try {
      setIsLoading(true);
      const turnoParaEnviar: TurnoPayload = {
      day: mapDiaToEnglish(dia), 
      start_hour: turno.desde.hours(),
      start_minute: turno.desde.minutes(),
      end_hour: turno.hasta.hours(),
      end_minute: turno.hasta.minutes(),
      address: {
        street: turno.direccion.calle,
        street_number : parseInt(String(turno.direccion.numero), 10) || 0,
        floor: turno.direccion.piso || "", 
        apartment: turno.direccion.departamento || "",
        locality: turno.direccion.localidad,
        district: turno.direccion.partido,
        province: turno.direccion.provincia,
        latitude: turno.direccion.latitud,
        longitude: turno.direccion.longitud
      }
    };
      if(turno.id !== 0){
        await editDriverTurnMutation.mutate(driverId, turno.id ,turnoParaEnviar);
      } else {
        await createDriverTurnMutation.mutate(driverId, turnoParaEnviar);
      }
      if (onTurnosUpdated) onTurnosUpdated();
    } catch (error) {
      console.log(error);
    } finally {
    setIsLoading(false);  }
  };

  const onDeleteTurn = async (dia: string, turnId: number) => {
    setIsLoading(true);
    try {
      await deleteDriverTurnMutation.mutate(driverId, turnId);
      await choferQuery.reload();
      if (onTurnosUpdated) onTurnosUpdated();
    } catch (error) {
      console.error("Error al eliminar turno:", error);
    } finally {
      setIsLoading(false);
    }
  };


  return (
    <Formik
      initialValues={initialValues}
      enableReinitialize={true} 
      validationSchema={Schema}
      onSubmit={() => {}}
    >
      {({ submitForm, touched, errors, values, validateForm, setTouched  }) => {

          const newTouched = { ...touched };
          let needsUpdate = false;

          dias.forEach(dia => {
            const currentTouchedLength = touched[dia]?.length || 0;
            const currentValuesLength = values[dia]?.length || 0;

            if (currentValuesLength > currentTouchedLength) {
              newTouched[dia] = [
                ...(touched[dia] || []),
                ...Array(currentValuesLength - currentTouchedLength)
                  .fill(null)
                  .map(() => ({ direccion: true, desde: true, hasta: true }))
              ];
              needsUpdate = true;
            }
          });
        
          if (needsUpdate) {
            setTouched(newTouched);
          }
 
       return <Form className="w-full grid gap-6">
          <p className="text-gray-700 font-bold text-lg">Horarios</p>
          <div className="space-y-2">
            {dias.map((dia) => (

              
              <div
                className="p-2 pt-0 border-b-2 border-blue-300   "
                key={dia}
              >
                <p className="text-gray-700 font-bold text-md mb-6">{dia.toUpperCase()}</p>
                <FieldArray
                  name={`${dia}`}
                  render={(arrayHelpers) => {
                    const primeraDireccion = values[`${dia}`][0]?.direccion;
                    const idConductor = values[`${dia}`][0]?.id_conductor;
                    return <div>
                      <FormRepeater
                      arrayHelpers={arrayHelpers}
                      removeButton={<button style={{ display: 'none' }} />}
                      addButton={<button style={{ display: 'none' }} />}
                      createField={() => {}}
                      className="flex flex-col gap-4 w-auto" 
                    >
                      {values[`${dia}`].map((value, index) => {
                        const turnId = values[`${dia}`][index]?.id;
                    return  <>
                        <div  key={index} className="flex flex-col xl:flex-row gap-4  ">
                          <div className="flex ">
                            <Field
                              name={`${dia}.${index}.direccion`}
                              component={FormikAddress}
                              className="col-span-2"
                              error={errors[`${dia}`]?.[index]?.direccion?.calle}
                            />
                          </div>
                          <div className='flex gap-4'>
                          <Field
                            component={KeyboardTimePicker}
                            label="Hora inicio turno"
                            name={`${dia}.${index}.desde`}
                            inputVariant="outlined"
                            ampm={false}
                            format="HH:mm"
                            fullWidth
                            error={errors[dia]?.[index]?.desde}
                      />
                         
                          <Field
                            component={KeyboardTimePicker}
                            label="Hora fin turno"
                            name={`${dia}.${index}.hasta`}
                            inputVariant="outlined"
                            ampm={false}
                            fullWidth
                            error={errors[dia]?.[index]?.hasta}
                          />
                          </div>
                        </div>
                        <Button
                          variant="contained"
                          color="red"
                          disabled={isLoading || (!!tieneSoloUnTurno && tieneSoloUnTurno.dia === dia && index === 0)}
                          onClick={() => {
                            
                            if (!turnId) {
                              arrayHelpers.remove(index);
                            } else {
                              onDeleteTurn(dia, turnId);    
                            }}}
                        >
                          <FontAwesomeIcon icon={faTrashAlt}  />
                        </Button>
                        <Button
                          variant="contained"
                          color="green"
                          disabled={isLoading || errors[dia]?.[index]?.direccion?.calle || errors[dia]?.[index]?.desde || errors[dia]?.[index]?.hasta}
                          onClick={() => onSubmitByTurn(dia, values[`${dia}`][index])}
                        >
                         {turnId ? <FontAwesomeIcon icon={faEdit}/> : 'Guardar'}
                        </Button>
                    </>
                      })}
                    
                    </FormRepeater>
                     <Button
                      type="button"
                      className="bg-blue-400 hover:bg-blue-500 text-white font-bold px-2 rounded"
                        disabled={
    values[dia]?.length > 0 && 
    values[dia][values[dia].length - 1].hasta?.isBefore(values[dia][values[dia].length - 1].desde)}
                      onClick={() => {
                        const nuevoTurno = initialTurn();
                        if (primeraDireccion || primeraDireccionDias) {
                         nuevoTurno.direccion = primeraDireccion || primeraDireccionDias?.direccion;
                          nuevoTurno.dia = dia;
                          nuevoTurno.id_conductor = idConductor;
                        }
                        arrayHelpers.push(nuevoTurno);

                        setTimeout(() => {
                          setTouched({
                            ...touched,
                            [dia]: [
                              ...(touched[dia] || []),
                              { direccion: true, desde: true, hasta: true }
                            ]
                          });
                        }, 0);
                      }}
                    >
                      + Turno
                    </Button>
                    </div>
                  }}
                />
                <p className="text-red-500">
                  {Array.isArray(errors[dia]) ? '' : errors[dia]}
                </p>
              </div>
            ))}
          </div>
          <div className="flex justify-between mt-8">
            {onGoBack ? (
              <Button
                type="button"
                variant="light"
                onClick={onGoBack}
                color="red"
              >
                {goBackButtonTitle}
              </Button>
            ) : (
              <div />
            )}
            {handleFinish && (
                <Button
                  disabled={isLoading || allDaysEmpty(initialValues)}
                  type="button"
                  variant="light"
                  color="green"
                  onClick={handleFinish}
                >
                  Finalizar
                </Button>
            )}
          </div>
        </Form>
}}
    </Formik>
  );
};

export default ChoferFormTurno;
